import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { MultiSVG } from './MultiSVG';
import { VariantSVG } from './VariantSVG';

const colorsToDisplay = 4;

const colorKeyValueMap = {
  blue: 'blå',
  beige: 'beige',
  red: 'rød',
  grey: 'grå',
  black: 'svart',
  brown: 'brun',
  orange: 'oransje',
  yellow: 'gul',
  green: 'grønn',
  purple: 'lilla',
  gold: 'gull',
  nature: 'natur',
  pink: 'rosa',
  silver: 'sølv',
  multi: 'flerfarget',
  white: 'hvit',
  clear: 'klar',
};
const colorNameToHexMap = {
  blue: 'bg-[#4D7B92]',
  beige: 'bg-[#F7F2EE]',
  red: 'bg-[#E11E26]',
  grey: 'bg-neutral-60',
  black: 'bg-[#071114]',
  brown: 'bg-[#6D5C50]',
  orange: 'bg-[#F48A33]',
  yellow: 'bg-[#F7C473]',
  green: 'bg-[#878B6F]',
  purple: 'bg-[#823EA4]',
  gold: 'bg-[#CAAB6A]',
  nature: 'bg-[#E3CEBC]',
  pink: 'bg-[#EDBFD8]',
  silver: 'bg-[#F5F5F5]',
  multi: '',
  white: 'bg-[#FCFBF9] border border-neutral-50',
  clear: 'bg-[#FFFFFF] border border-neutral-80',
};

const colorNameToHex = (colorName: string) => {
  return colorNameToHexMap[colorName as keyof typeof colorNameToHexMap] ?? '';
};

const colorNameToKey = (colorName: string) => {
  return (
    Object.keys(colorKeyValueMap).find(
      (key) =>
        colorKeyValueMap[key as keyof typeof colorKeyValueMap] ===
        colorName.toLowerCase()
    ) ?? ''
  );
};

type ProductListGridItemColorVariantsProps = {
  activeColor: string;
  colors: string[];
};

export const ProductListGridItemColorVariants = ({
  activeColor,
  colors,
}: ProductListGridItemColorVariantsProps) => {
  const colorCount = colors.length;

  const mappedActiveColor = colorNameToKey(activeColor);

  const mappedColors = colors
    .map((color) => {
      return colorNameToKey(color);
    })
    .filter(Boolean)
    .filter((color) => color !== mappedActiveColor)
    .slice(0, colorsToDisplay);

  const additionalColors = colorCount - mappedColors.length;

  return (
    <div className="flex flex-wrap gap-1">
      {mappedActiveColor && (
        <div className="size-5 rounded-full overflow-hidden bg-secondary-60 flex items-center justify-center">
          <div className="size-[18px] bg-white rounded-full flex items-center justify-center">
            {mappedActiveColor === 'multi' ? (
              <MultiSVG className="size-[14px] rounded-full" />
            ) : (
              <div
                className={twMerge(
                  clsx(
                    'size-[14px] rounded-full',
                    colorNameToHex(mappedActiveColor)
                  )
                )}
              ></div>
            )}
          </div>
        </div>
      )}
      {mappedColors.map((color, index) => {
        if (color === 'multi') {
          return <MultiSVG key={index} className="size-5" />;
        }
        return (
          <div
            key={index}
            className={twMerge(
              clsx('size-5 rounded-full overflow-hidden', colorNameToHex(color))
            )}
          ></div>
        );
      })}
      {mappedColors.length === 0 && <VariantSVG className="size-5" />}
      {additionalColors > 0 && (
        <div className="h-5 flex items-center justify-center">
          <span className="body-xs">+{additionalColors} farger</span>
        </div>
      )}
    </div>
  );
};
